<template>
  <div class="bg-white">
    <main>
      <section class="py-20">
        <div class="container mx-auto max-w-6xl px-4">
          <div class="mx-auto max-w-3xl text-center">
            <h1 class="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
              Digital Software Partner Program
            </h1>
            <p class="mt-6 text-lg leading-7 text-gray-600">
              Earn up to 30% commission promoting digital software solutions worldwide.
            </p>
            <div class="mt-10 flex items-center justify-center">
              <NuxtLink
                to="/partner-apply"
                class="inline-flex items-center justify-center rounded-xl bg-gray-900 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
              >
                Become a Partner
              </NuxtLink>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 bg-gray-50">
        <div class="container mx-auto max-w-6xl px-4">
          <div class="mx-auto max-w-3xl text-center">
            <h2 class="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">How it works</h2>
            <p class="mt-4 text-base leading-7 text-gray-600">
              A simple, transparent process designed to help you start earning quickly.
            </p>
          </div>

          <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">1. Sign up</div>
              <div class="mt-2 text-sm leading-6 text-gray-600">Apply and tell us about your audience.</div>
            </div>
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">2. Get your affiliate link</div>
              <div class="mt-2 text-sm leading-6 text-gray-600">Receive a unique tracking link to share.</div>
            </div>
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">3. Share with your audience</div>
              <div class="mt-2 text-sm leading-6 text-gray-600">Promote products that match your niche.</div>
            </div>
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">4. Earn commission per sale</div>
              <div class="mt-2 text-sm leading-6 text-gray-600">Get rewarded for every successful purchase.</div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20">
        <div class="container mx-auto max-w-6xl px-4">
          <div class="grid gap-10 lg:grid-cols-2 lg:items-start">
            <div>
              <h2 class="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">Commissions</h2>
              <p class="mt-4 text-base leading-7 text-gray-600">
                Earn more as you grow. Tiers are based on performance and consistency.
              </p>

              <div class="mt-8 overflow-hidden rounded-xl shadow-sm ring-1 ring-gray-200">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th scope="col" class="px-6 py-4 text-left text-sm font-semibold text-gray-900">Partner Tier</th>
                      <th scope="col" class="px-6 py-4 text-right text-sm font-semibold text-gray-900">Commission</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 bg-white">
                    <tr>
                      <td class="px-6 py-4 text-sm text-gray-900">Standard Partner</td>
                      <td class="px-6 py-4 text-right text-sm font-semibold text-gray-900">20%</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 text-sm text-gray-900">Active Partner</td>
                      <td class="px-6 py-4 text-right text-sm font-semibold text-gray-900">25%</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 text-sm text-gray-900">Top Partner</td>
                      <td class="px-6 py-4 text-right text-sm font-semibold text-gray-900">30%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <dl class="mt-8 grid gap-6 sm:grid-cols-2">
                <div class="rounded-xl bg-gray-50 p-6 ring-1 ring-gray-200">
                  <dt class="text-sm font-semibold text-gray-900">Cookie duration</dt>
                  <dd class="mt-2 text-sm text-gray-600">30 days</dd>
                </div>
                <div class="rounded-xl bg-gray-50 p-6 ring-1 ring-gray-200">
                  <dt class="text-sm font-semibold text-gray-900">Payout</dt>
                  <dd class="mt-2 text-sm text-gray-600">Monthly</dd>
                </div>
              </dl>
            </div>

            <div>
              <h2 class="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">Who can join</h2>
              <p class="mt-4 text-base leading-7 text-gray-600">
                If you reach people who buy or use software, you’re a great fit.
              </p>

              <ul class="mt-8 grid gap-3">
                <li class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">Tech YouTubers</li>
                <li class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">Software bloggers</li>
                <li class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">IT consultants</li>
                <li class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">Tech communities</li>
                <li class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">Developers</li>
                <li class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">Entrepreneurs</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 bg-gray-50">
        <div class="container mx-auto max-w-6xl px-4">
          <div class="mx-auto max-w-3xl text-center">
            <h2 class="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">Benefits</h2>
            <p class="mt-4 text-base leading-7 text-gray-600">
              Promote products customers trust, with support from our team.
            </p>
          </div>

          <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">High conversion products</div>
              <div class="mt-2 text-sm leading-6 text-gray-600">Popular digital software solutions with proven demand.</div>
            </div>
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">Global audience</div>
              <div class="mt-2 text-sm leading-6 text-gray-600">Worldwide reach with localized customer experience.</div>
            </div>
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">Reliable monthly payouts</div>
              <div class="mt-2 text-sm leading-6 text-gray-600">Clear terms, recurring evaluation, consistent payments.</div>
            </div>
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">Technical support included</div>
              <div class="mt-2 text-sm leading-6 text-gray-600">We help customers post-purchase, reducing your support load.</div>
            </div>
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">Fast digital delivery</div>
              <div class="mt-2 text-sm leading-6 text-gray-600">Instant fulfillment and straightforward onboarding.</div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20">
        <div class="container mx-auto max-w-6xl px-4">
          <div class="mx-auto max-w-3xl text-center">
            <h2 class="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">FAQ</h2>
            <p class="mt-4 text-base leading-7 text-gray-600">Answers to the most common questions.</p>
          </div>

          <div class="mt-12 grid gap-6 lg:grid-cols-3">
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">How do I track my sales?</div>
              <p class="mt-2 text-sm leading-6 text-gray-600">
                You’ll receive a unique affiliate link, and sales will be tracked automatically through it.
              </p>
            </div>
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">When are commissions paid?</div>
              <p class="mt-2 text-sm leading-6 text-gray-600">
                Commissions are paid monthly, after successful payments are confirmed.
              </p>
            </div>
            <div class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
              <div class="text-sm font-semibold text-gray-900">Is there a minimum payout?</div>
              <p class="mt-2 text-sm leading-6 text-gray-600">
                No minimum payout. If you have commissions for the month, you’ll be eligible for payout.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 bg-gray-900">
        <div class="container mx-auto max-w-6xl px-4">
          <div class="mx-auto max-w-3xl text-center">
            <h2 class="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Start earning with our partner program today.
            </h2>
            <p class="mt-4 text-base leading-7 text-gray-300">
              Apply now and get your affiliate link to start promoting digital software worldwide.
            </p>
            <div class="mt-10 flex items-center justify-center">
              <NuxtLink
                to="/partner-apply"
                class="inline-flex items-center justify-center rounded-xl bg-white px-6 py-3 text-sm font-semibold text-gray-900 shadow-sm transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-900"
              >
                Apply Now
              </NuxtLink>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
useHead({
  title: 'Digital Software Partner Program | Casa do Software',
  meta: [
    {
      name: 'description',
      content: 'Join our partner program and earn up to 30% commission promoting digital software solutions.',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://casadosoftware.com.br/en/become-a-partner',
    },
  ],
})
</script>
